import { useCallback, useEffect, useState } from 'react';
import Highcharts from 'highcharts';
import HighchartsReact from 'highcharts-react-official';
import { useNavigate } from 'react-router';
import { Grid, IconButton, Paper, Tooltip, Typography } from '@mui/material';
import InfoIcon from '@mui/icons-material/Info';
import Box from '@mui/material/Box';
import CircularProgress from '@mui/material/CircularProgress';

import { useHedisMetricsContext } from '../../../hooks/hedisMetricsContext';
import { configureStackedBarChartWithoutLegend } from '../../ChartConfiguration/ChartConfiguration';
import { getBpSummary } from '../HedisMetricService';
import BpSummary from '../../../Models/HedisMetrics/BpSummary.model';
import { TimePeriodFilterEnum } from '../../../Enum/TimePeriodFilterEnum';
import { HedisMetricsTypeEnum } from '../../../Enum/HedisMetricsTypeEnum';
import FilterOptions from '../FilterOptions';
import '../../Widgets/Widgets.css';

const BpSummaries = (props) => {
    const history = useNavigate();
    const [isFetched, setIsFetched] = useState<boolean>(false);
    const [isGraphShow, setIsGraphShow] = useState<boolean>(false);
    const [isPrepared, setIsPrepared] = useState<boolean>(false);
    const [bpSummary, setBpSummary] = useState<BpSummary>();
    const [chartOptions, setChartOptions] = useState({});
    const [show, setShow] = useState(false);
    const {
        bpHealthPlans,
        setBpHealthPlans,
        bpPopulation,
        setBpPopulation,
        bpTeam,
        setBpTeam,
        bpDateFilter,
        setBpDateFilter,
        bpFromDate,
        setBpFromDate,
        bpToDate,
        setBpToDate,
        bpIncludePastOpenStatus,
        setBpIncludePastOpenStatus,
        bpShowInActiveTeam,
        setBpShowInActiveTeam,
        setHedisType,
        setHealthPlans,
        setPopulation,
        setTeam,
        setFilter,
        setFDate,
        setTDate,
        setIncludePastOpenStatus,
        setShowInActiveTeam,
        setHasElevatedFilterAccess,
    } = useHedisMetricsContext();
    const chartHeight = 450;

    const initialPage = useCallback(async () => {
        if (isFetched) {
            return;
        }

        if (!isGraphShow) {
            setChartOptions(
                configureStackedBarChartWithoutLegend('BP Summary', 'Controlled vs Not Controlled', '', 'Count', [{ name: '', data: [0] }], [''], null, chartHeight)
            );
        }

        setIsFetched(true);
    }, [isFetched, setIsFetched, isGraphShow]);

    useEffect(() => {
        initialPage();
    }, [initialPage]);

    useEffect(() => {
        if (bpDateFilter !== TimePeriodFilterEnum.Custom || (Boolean(bpFromDate) && Boolean(bpToDate))) {
            const changeFilter = async () => {
                let start = bpFromDate !== null ? new Date(bpFromDate).toDateString() : null;
                let end = bpToDate !== null ? new Date(bpToDate).toDateString() : null;

                const data = await getBpSummary(bpHealthPlans, bpPopulation, bpTeam, bpDateFilter, start, end, bpIncludePastOpenStatus, bpShowInActiveTeam);
                setBpSummary(data);

                setIsPrepared(true);
            };
            if ((bpFromDate !== null || bpToDate !== null) && bpToDate < bpFromDate) {
            } else {
                setChartOptions(
                    configureStackedBarChartWithoutLegend(
                        'BP Summary',
                        'Controlled vs Not Controlled',
                        '',
                        'Count',
                        [{ name: '', data: [0], showInLegend: false, title: { text: null } }],
                        [''],
                        null,
                        chartHeight
                    )
                );
                setIsGraphShow(false);
                changeFilter();
            }
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [bpHealthPlans, bpPopulation, bpTeam, bpDateFilter, bpFromDate, bpToDate, bpIncludePastOpenStatus, bpShowInActiveTeam]);

    useEffect(() => {
        if (bpSummary && isPrepared) {
            let xAxisValues = [''];
            let yAxisValues = [];
            let yValues = [];
            let yVal;

            yValues = [];
            yVal = {
                y: bpSummary.ControlledBp,
                name: 'Controlled Bp',
            };
            yValues.push(yVal);
            yAxisValues.push({ name: 'Controlled Bp', data: yValues, pointPadding: 0, color: '#9ec7e8', showInLegend: true });

            yValues = [];
            yVal = {
                y: bpSummary.NotControlledBp,
                name: 'Not Controlled Bp',
            };
            yValues.push(yVal);
            yAxisValues.push({ name: 'Not Controlled Bp', data: yValues, pointPadding: 0, color: '#6495ED', showInLegend: true });

            if (bpSummary.ControlledBp === 0 && bpSummary.NotControlledBp === 0) {
                setChartOptions(
                    configureStackedBarChartWithoutLegend(
                        'BP Summary',
                        'Controlled vs Not Controlled',
                        '',
                        'Count',
                        [{ name: '', data: [], showInLegend: false, title: { text: null } }],
                        [0],
                        null,
                        chartHeight
                    )
                );
            } else {
                setChartOptions(configureStackedBarChartWithoutLegend('BP Summary', '', '', 'Count', yAxisValues, xAxisValues, navigateToMembers, chartHeight));
            }

            setIsGraphShow(true);
            setIsPrepared(false);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [bpSummary, bpHealthPlans, isPrepared]);

    const handleClick = (event) => {
        setShow(!show);
    };

    const navigateToMembers = () => {
        setHealthPlans(bpHealthPlans);
        setPopulation(bpPopulation);
        setTeam(bpTeam);
        setFilter(bpDateFilter);
        setFDate(bpFromDate);
        setTDate(bpToDate);
        setIncludePastOpenStatus(bpIncludePastOpenStatus);
        setShowInActiveTeam(bpShowInActiveTeam);
        setHedisType(HedisMetricsTypeEnum.BP);
        setHasElevatedFilterAccess(props.hasElevatedFilterAccess);
        history(`/provider/metricsummary/hedismemberdetail/BP`);
    };

    return (
        <Grid size={{ xs: 12, lg: 6 }}>
            <Paper className="smartdashboard-widget" elevation={5} sx={{ height: 'calc(100vh - 140px)', maxHeight: '650px', display: 'flex', flexDirection: 'column', overflow: 'auto' }}>
                <Grid container spacing={2} paddingLeft={2} paddingRight={2}>
                    <Grid size={12} textAlign="center">
                        <Typography display={'inline-block'} variant="h6" color={'primary'} onClick={navigateToMembers} className="pointer">
                            BP
                        </Typography>
                        <Tooltip
                            open={show}
                            title={
                                <div>
                                    <Typography variant="body2">Member who has an ICD 10 of I10, within a rolling 12 months</Typography>

                                    <Typography marginTop={1} fontWeight={800} variant="subtitle2">
                                        Controlled BP :
                                    </Typography>

                                    <Typography variant="caption">
                                        CPT Codes 3074F or 3075F Systolic (must have one or the other of these) and 3078F or 3079F Diastolic (must have one or
                                        the other of these)
                                    </Typography>

                                    <Typography marginTop={1} fontWeight={800} variant="subtitle2">
                                        Not Controlled Bp :
                                    </Typography>

                                    <Typography variant="caption">CPT Codes 3077F and 3080F</Typography>
                                </div>
                            }
                            PopperProps={{ style: { zIndex: 1000 } }}
                        >
                            <IconButton onClick={handleClick}>
                                <InfoIcon sx={{ width: '18px', height: '18px' }} />
                            </IconButton>
                        </Tooltip>
                    </Grid>
                    <FilterOptions
                        selectedTeam={bpTeam}
                        setSelectedTeam={setBpTeam}
                        dateFilter={bpDateFilter}
                        setDateFilter={setBpDateFilter}
                        fromDate={bpFromDate}
                        setFromDate={setBpFromDate}
                        toDate={bpToDate}
                        setToDate={setBpToDate}
                        healthPlan={bpHealthPlans}
                        setHealthPlan={setBpHealthPlans}
                        pop={bpPopulation}
                        setPop={setBpPopulation}
                        includePastOpenStatusMembers={bpIncludePastOpenStatus}
                        setIncludePastOpenStatusMembers={setBpIncludePastOpenStatus}
                        hedisType={HedisMetricsTypeEnum.BP}
                        showInActiveTeams={bpShowInActiveTeam}
                        setShowInActiveTeams={setBpShowInActiveTeam}
                        hasElevatedFilterAccess={props.hasElevatedFilterAccess}
                    />
                    <Grid size={12} sx={{ position: 'relative' }}>
                        {!isGraphShow ? (
                            <Box className="loader-center">
                                <CircularProgress />
                            </Box>
                        ) : null}
                        <HighchartsReact highcharts={Highcharts} options={chartOptions} />
                    </Grid>
                </Grid>
            </Paper>
        </Grid>
    );
};

export default BpSummaries;
